import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SubscriptionManagement.css";
import useAuthStore from "../../stores/authStore";
import { useQuota } from "../../context/QuotaContext";
import CancelSubscriptionDialog from "../../components/CancelSubscriptionDialog/CancelSubscriptionDialog";

const API_URL = process.env.REACT_APP_API_URL;

const SubscriptionManagement = () => {
  const { user } = useAuthStore();
  const { quota, fetchQuota } = useQuota();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchQuota();
        await fetchSubscriptionDetails();
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchQuota]);

  const fetchSubscriptionDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/me/current`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSubscription(data.subscription);
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
    }
  };

  const handlePlanUpgrade = async (newPlan, newDuration) => {
    if (!subscription) return;

    setUpdating(true);
    try {
      // For upgrades, create a new checkout session instead of updating directly
      const response = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          plan_name: newPlan,
          duration: newDuration,
          success_url: `${window.location.origin}/dashboard/subscription?upgraded=true`,
          cancel_url: `${window.location.origin}/dashboard/subscription`,
          billing: {
            street: "123 Main Street",
            city: "New York",
            state: "NY",
            zipcode: "10001",
            country: "US"
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to checkout
        window.location.href = data.checkout_url;
      } else {
        const errorData = await response.json();
        alert(errorData.detail || "Failed to start upgrade process");
      }
    } catch (error) {
      console.error("Error starting upgrade:", error);
      alert("Failed to start upgrade process. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelSubscription = () => {
    setShowCancelDialog(true);
  };

  const getCurrentPlanInfo = () => {
    if (!quota?.plan_name) return { name: "Free", duration: null };
    
    const planParts = quota.plan_name.split("-");
    return {
      name: planParts[0],
      duration: planParts[1] || "monthly",
    };
  };

  const getAvailablePlans = () => {
    const currentPlan = getCurrentPlanInfo();
    const plans = [
      { name: "starter", displayName: "Starter", monthly: 14, yearly: 168, level: 1 },
      { name: "creator", displayName: "Creator", monthly: 24, yearly: 288, level: 2 },
      { name: "pro", displayName: "Pro", monthly: 64, yearly: 768, level: 3 },
    ];

    // If subscription is cancelled or inactive, show all plans
    if (!quota?.plan_is_active || quota?.subscription_status === "cancelled") {
      return plans;
    }

    // Only show plans that are upgrades (higher level) than current plan
    const currentLevel = plans.find(plan => plan.name === currentPlan.name)?.level || 0;
    return plans.filter(plan => plan.level > currentLevel);
  };

  if (loading) {
    return (
      <div className="subscription-management">
        <div className="loading">Loading subscription details...</div>
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  const currentPlan = getCurrentPlanInfo();
  const availablePlans = getAvailablePlans();

  return (
    <div className="subscription-management">
      <div className="subscription-header">
        <h1>Subscription Management</h1>
        <p>Manage your Brandly subscription and billing</p>
      </div>

      <div className="current-subscription">
        <h2>Current Plan</h2>
        <div className="plan-card current">
          <div className="plan-info">
            <h3>{currentPlan.name.charAt(0).toUpperCase() + currentPlan.name.slice(1)}</h3>
            <p className="plan-duration">
              {currentPlan.duration ? `${currentPlan.duration.charAt(0).toUpperCase() + currentPlan.duration.slice(1)}` : "Free Plan"}
            </p>
            <p className="plan-status">
              Status: <span className={`status ${
                quota?.subscription_status === "cancelled" ? "cancelled" :
                quota?.plan_is_active ? "active" : "inactive"
              }`}>
                {quota?.subscription_status === "cancelled" ? "Cancelled" : 
                 quota?.plan_is_active ? "Active" : "Inactive"}
              </span>
            </p>
            {subscription?.next_billing_date && (
              <p className="next-billing">
                Next billing: {new Date(subscription.next_billing_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {quota?.plan_name && quota.plan_name !== "free" && availablePlans.length > 0 && (
        <div className="plan-options">
          <h2>
            {!quota?.plan_is_active || quota?.subscription_status === "cancelled" 
              ? "Choose a New Plan" 
              : "Upgrade Your Plan"
            }
          </h2>
          <p>
            {!quota?.plan_is_active || quota?.subscription_status === "cancelled"
              ? "Reactivate your subscription or choose a different plan."
              : "Unlock more features and higher limits with a plan upgrade."
            }
          </p>
          <div className="plans-grid">
            {availablePlans.map((plan) => {
              const isUpgrade = quota?.plan_is_active && quota?.subscription_status !== "cancelled";
              const currentLevel = quota?.plan_is_active ? 
                [{ name: "starter", level: 1 }, { name: "creator", level: 2 }, { name: "pro", level: 3 }]
                  .find(p => p.name === getCurrentPlanInfo().name)?.level || 0 : 0;
              const isHigherLevel = plan.level > currentLevel;
              
              return (
                <div key={plan.name} className={`plan-card ${isUpgrade && isHigherLevel ? 'upgrade' : 'available'}`}>
                  <div className="plan-info">
                    <h3>{plan.displayName}</h3>
                    <div className="pricing">
                      <div className="monthly">
                        <span className="price">${plan.monthly}</span>
                        <span className="period">/month</span>
                      </div>
                      <div className="yearly">
                        <span className="price">${plan.yearly}</span>
                        <span className="period">/year</span>
                        <span className="savings">Save 2 months</span>
                      </div>
                    </div>
                  </div>
                  <div className="plan-actions">
                    <button
                      className="btn btn-outline"
                      onClick={() => handlePlanUpgrade(plan.name, "monthly")}
                      disabled={updating}
                    >
                      {isUpgrade && isHigherLevel ? "Upgrade to Monthly" : "Get Monthly"}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handlePlanUpgrade(plan.name, "yearly")}
                      disabled={updating}
                    >
                      {isUpgrade && isHigherLevel ? "Upgrade to Yearly" : "Get Yearly"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {quota?.plan_name && quota.plan_name !== "free" && availablePlans.length === 0 && (
        <div className="max-plan-section">
          <h2>You're on our highest plan!</h2>
          <p>You're already on the Pro plan with unlimited features. Enjoy all the benefits!</p>
        </div>
      )}

      {quota?.plan_name && quota.plan_name !== "free" && (
        <div className="downgrade-section">
          <h2>Need to Downgrade?</h2>
          <p>To downgrade your plan, please contact our support team. We'll help you find the right plan for your needs.</p>
          <a href="mailto:support@brandly.com" className="btn btn-outline">
            Contact Support
          </a>
        </div>
      )}

      {quota?.plan_name && quota.plan_name !== "free" && (
        <div className="cancellation-section">
          <h2>Cancel Subscription</h2>
          <p>We're sorry to see you go. You can cancel your subscription at any time.</p>
          <button
            className="btn btn-danger"
            onClick={handleCancelSubscription}
            disabled={updating}
          >
            Cancel Subscription
          </button>
        </div>
      )}

      {quota?.plan_name === "free" && (
        <div className="upgrade-section">
          <h2>Upgrade Your Plan</h2>
          <p>Unlock more features and higher limits with a paid subscription.</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/pricing")}
          >
            View Plans
          </button>
        </div>
      )}

      <CancelSubscriptionDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onCancel={handleCancelSubscription}
        currentPlan={currentPlan}
      />
    </div>
  );
};

export default SubscriptionManagement;
